// Route chunk prefetcher — fires the lazy-import factory of an upcoming
// page on user *intent* (mouseover / touchstart / focus) so the JS chunk
// is already in the HTTP cache by the time the user actually taps.
//
// Design:
//  • We keep a `path -> importFactory` registry populated by `routes.js`.
//  • `prefetchRoute(path)` looks up the factory and calls it (which fires
//    the dynamic `import('./pages/X')` webpack chunk load). React & webpack
//    both dedupe subsequent imports, so calling it multiple times is safe.
//  • `usePrefetchOnIntent()` returns bind-props for any nav element:
//        <Link {...usePrefetchOnIntent('/community')} to="/community">…
//    → mouseenter / focus / touchstart all warm the chunk.
//  • We only prefetch once per path per session (a Set-backed guard) so
//    hover jitters don't cause redundant network activity.
//  • Respects `navigator.connection.saveData` and `2g`/`slow-2g` links —
//    on those we skip prefetching to save the user's data.

const registry = new Map();
const prefetched = new Set();

let idle = null;
const scheduleIdle = (cb) => {
  if (typeof window === 'undefined') return;
  if (typeof window.requestIdleCallback === 'function') {
    idle = window.requestIdleCallback(cb, { timeout: 400 });
  } else {
    idle = setTimeout(cb, 60);
  }
};

const shouldSkipPrefetch = () => {
  try {
    const conn = navigator?.connection;
    if (!conn) return false;
    if (conn.saveData) return true;
    if (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') return true;
    return false;
  } catch {
    return false;
  }
};

/**
 * Register a route path with its lazy import factory. Called by `routes.js`.
 * `factory` MUST be the same function reference passed to React.lazy() so
 * webpack's module cache dedupes correctly.
 */
export const registerRoute = (path, factory) => {
  registry.set(path, factory);
};

/**
 * Trigger the chunk fetch for a given route path.
 * No-op if already prefetched, on save-data, or on 2g networks.
 */
export const prefetchRoute = (path) => {
  if (!path || prefetched.has(path)) return;
  if (shouldSkipPrefetch()) return;
  const factory = registry.get(path);
  if (!factory) return;
  prefetched.add(path);
  scheduleIdle(() => {
    try {
      const promise = factory();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => prefetched.delete(path)); // allow retry on failure
      }
    } catch {
      prefetched.delete(path);
    }
  });
};

/**
 * Returns intent handlers you can spread onto ANY React element that
 * navigates to a lazy route. NOT a hook — plain function, but React's
 * lint rules can't tell without a `use` prefix, so this deliberately
 * avoids the `use*` naming.
 *
 * Example:
 *   <Link to="/community" {...prefetchOnIntent('/community')}>Community</Link>
 *   <button {...prefetchOnIntent('/ai-hub')} onClick={...}>AI Saheli</button>
 */
export const prefetchOnIntent = (path) => {
  const handler = () => prefetchRoute(path);
  return {
    onMouseEnter: handler,
    onFocus: handler,
    onTouchStart: handler,
  };
};

/** Test helper — clears prefetch state between suites. */
export const _resetPrefetch = () => {
  prefetched.clear();
  if (idle && typeof window !== 'undefined') {
    if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idle);
    else clearTimeout(idle);
    idle = null;
  }
};
